import React, { useState, useEffect } from 'react';
import { FaStar, FaTimes, FaCommentAlt, FaPaperPlane } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';

const API = '';

export function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [liked, setLiked] = useState('');
  const [confusing, setConfusing] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Listen for custom event to open feedback from Header
  useEffect(() => {
    const handleOpenFeedback = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('openFeedback', handleOpenFeedback);
    return () => window.removeEventListener('openFeedback', handleOpenFeedback);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/feedback`, {
        rating,
        liked: liked.trim() || null,
        confusing: confusing.trim() || null,
        suggestions: suggestions.trim() || null,
        page_context: window.location.pathname
      });
      
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
      
      // Reset after 3 seconds and close
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setRating(0);
        setLiked('');
        setConfusing('');
        setSuggestions('');
      }, 2000);
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            data-testid="feedback-modal"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white relative">
              <button 
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition"
              >
                <FaTimes className="text-xl" />
              </button>
              <h2 className="text-2xl font-bold">Share Your Feedback</h2>
              <p className="text-purple-100 mt-1">Help us improve your experience!</p>
            </div>

            {submitted ? (
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaStar className="text-green-500 text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your feedback helps us make the app better for everyone.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Star Rating */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    How would you rate your experience? *
                  </label>
                  <div className="flex justify-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`rating-star-${star}`}
                      >
                        <FaStar 
                          className={`text-4xl transition-colors ${
                            star <= (hoverRating || rating) 
                              ? 'text-yellow-400' 
                              : 'text-gray-300'
                          }`} 
                        />
                      </button>
                    ))}
                  </div>
                  {rating > 0 && (
                    <p className="text-center text-sm text-gray-500 mt-2">
                      {rating === 1 && 'Poor'}
                      {rating === 2 && 'Fair'}
                      {rating === 3 && 'Good'}
                      {rating === 4 && 'Very Good'}
                      {rating === 5 && 'Excellent!'}
                    </p>
                  )}
                </div>

                {/* What did you like? */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What did you like? 
                  </label>
                  <textarea
                    value={liked}
                    onChange={(e) => setLiked(e.target.value)}
                    placeholder="Tell us what's working well..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    data-testid="feedback-liked-input"
                  />
                </div>

                {/* What was confusing? */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What was confusing?
                  </label>
                  <textarea
                    value={confusing}
                    onChange={(e) => setConfusing(e.target.value)}
                    placeholder="Anything that was hard to understand..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    data-testid="feedback-confusing-input"
                  />
                </div>

                {/* Suggestions */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Any suggestions?
                  </label>
                  <textarea
                    value={suggestions}
                    onChange={(e) => setSuggestions(e.target.value)}
                    placeholder="Ideas for improvement..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    data-testid="feedback-suggestions-input"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting || rating === 0}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  data-testid="submit-feedback-btn"
                >
                  {submitting ? (
                    <span>Submitting...</span>
                  ) : (
                    <>
                      <FaPaperPlane />
                      <span>Submit Feedback</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
